// src/modules/app-views/goodsReceipt/pages/Document/modals/ItemModal/index.jsx
// *******************************************************************************
// GOODS RECEIPT ITEM MODAL
// *******************************************************************************
// 4-step GenericModal for adding/editing goods receipt items.
// Step 1: NF item data (description, code, NCM, CFOP, unit, qty, value, conversion)
// Step 2: Fiscal data (ICMS, ICMS-ST, IPI, PIS, COFINS)
// Step 3: Product & supplier (name, sku, brand, cost/margin, supplier ref)
// Step 4: Stock distribution (warehouse allocation, lot, cost summary)
// Includes ProductsParametersModal for inline unit/brand/category creation.
// On finish, saves item data and optionally creates/updates the linked product.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useEffect } from 'react';
import { ShoppingCartOutlined, EditOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import financialService from 'services/financialService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import GoodsReceiptParametersModal from 'modules/app-views/goodsReceipt/pages/Init/modals/GoodsReceiptParametersModal';

// NOTE: Product creation and supplierRef upsert are deferred to confirm().
// The item modal only captures data — no side effects on products.
import productStep from './Step1';
import stockDistributionStep from './Step4Stock';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ItemModal = ({ show, setShow, onConfirm, onClose, editingItem, issuerId, documentType }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!editingItem;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [autoLinked, setAutoLinked] = useState(false);
  // ProductsParametersModal state (for inline unit/brand/category creation)
  const [showParametersModal, setShowParametersModal] = useState(false);
  const [parametersInitialTab, setParametersInitialTab] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!show) {
      setSelectedProduct(null);
      setAutoLinked(false);
    } else if (editingItem?.linkedProductId) {
      // Fetch real product data for SimpleSelectAsyncProducts display
      setAutoLinked(true);
      fetchProductForDisplay(editingItem.linkedProductId).then(product => {
        if (product) {
          setSelectedProduct(product);
        } else {
          // Fallback if fetch fails
          setSelectedProduct({
            _id: editingItem.linkedProductId,
            name: editingItem.description,
          });
        }
      });
    }
  }, [show, editingItem]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOpenParametersModal = useCallback(tab => {
    setParametersInitialTab(tab || null);
    setShowParametersModal(true);
  }, []);

  const handleFinish = useCallback(
    async ({ form }) => {
      const values = form.getFieldsValue(true);

      const quantity = values.quantity || 1;
      const unitValue = values.unitValue || 0;
      const discount = values.discount || { init: 'percentage', percentage: 0, value: 0 };
      const discountValue = discount.value || 0;
      const discountPercentage = discount.percentage || 0;
      const subtotal = financialService.roundCurrency(quantity * unitValue);
      const totalValue = financialService.roundCurrency(Math.max(0, subtotal - discountValue));

      // Conversion
      const conversionFactor = values.conversionFactor || 1;
      const convertedQuantity = financialService.roundCurrency(quantity * conversionFactor);
      const convertedUnitCost =
        conversionFactor > 0 ? financialService.roundCurrency(unitValue / conversionFactor) : 0;

      // Build item data for the goods receipt
      // Product creation and supplierRef upsert are deferred to confirm()
      const itemData = {
        // Step 1 - NF item data
        linkedProductId: values.linkedProductId || null,
        productCode: values.productCode || null,
        description: values.description,
        ncm: values.ncm || null,
        cfop: values.cfop || null,
        taxSituation: values.taxSituation || null,
        purchaseUnitUid: values.purchaseUnitUid || null,
        saleUnitUid: values.saleUnitUid || null,
        quantity,
        unitValue,
        discount,
        discountValue,
        discountPercentage,
        totalValue,
        // Conversion
        conversionFactor,
        convertedQuantity,
        convertedUnitCost,
        // Step 2 - Stock distribution
        stockDistribution: values.stockDistribution || [],
      };

      onConfirm?.(itemData);
      return true;
    },
    [onConfirm],
  );

  const handleAfterClose = useCallback(() => {
    setSelectedProduct(null);
    onClose?.();
  }, [onClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => {
    if (editingItem) {
      return {
        // Step 1
        linkedProductId: editingItem.linkedProductId,
        productCode: editingItem.productCode,
        description: editingItem.description,
        purchaseUnitUid: editingItem.purchaseUnitUid || null,
        ncm: editingItem.ncm,
        cfop: editingItem.cfop,
        taxSituation: editingItem.taxSituation || null,
        quantity: editingItem.quantity || 1,
        unitValue: editingItem.unitValue || 0,
        discount: editingItem.discount || { init: 'percentage', percentage: 0, value: 0 },
        totalValue: editingItem.totalValue || 0,
        saleUnitUid: editingItem.saleUnitUid || null,
        conversionFactor: editingItem.conversionFactor || 1,
        convertedQuantity: editingItem.convertedQuantity || editingItem.quantity || 0,
        convertedUnitCost: editingItem.convertedUnitCost || editingItem.unitValue || 0,
        // Step 2 - Stock distribution
        stockDistribution: editingItem.stockDistribution || [],
      };
    }

    return {
      quantity: 1,
      unitValue: 0,
      discount: { init: 'percentage', percentage: 0, value: 0 },
      totalValue: 0,
      conversionFactor: 1,
      stockDistribution: [],
    };
  }, [editingItem]);

  const steps = useMemo(
    () => [
      productStep({
        selectedProduct,
        setSelectedProduct,
        onOpenParametersModal: handleOpenParametersModal,
        issuerId,
        documentType,
        autoLinked,
        setAutoLinked,
      }),
      stockDistributionStep(),
    ],
    [selectedProduct, handleOpenParametersModal, issuerId, documentType, autoLinked],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <GenericModal
        show={show}
        setShow={setShow}
        title={isEditing ? 'edit_item' : 'add_item'}
        icon={isEditing ? <EditOutlined /> : <ShoppingCartOutlined />}
        steps={steps}
        onFinish={handleFinish}
        afterClose={handleAfterClose}
        width={720}
        initialValues={initialValues}
      />

      {/* Shared ProductsParametersModal for inline unit/brand/category creation */}
      <GoodsReceiptParametersModal
        show={showParametersModal}
        setShow={setShowParametersModal}
        initialTab={parametersInitialTab}
        resetInitialTab={() => setParametersInitialTab(null)}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ItemModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Fetch product by ID for display in SimpleSelectAsyncProducts.
 * Returns the full product object with real catalog data (name, sku, price, unitUid, suppliers).
 */
async function fetchProductForDisplay(productId) {
  try {
    const url = API.PRODUCTS.URL.replace(':_id?', productId);
    const [product] = await axiosService.get({ url, returnAsObject: true });
    return product || null;
  } catch {
    return null;
  }
}
